import useCurrentPage from '@/hooks/use-current-page'
import ProductPageUiInfoTable from './ProductPageUiInfoTable/ProductPageUiInfoTable'
import ProductPageUiLeftSide from './ProductPageUiLeftSide/ProductPageUiLeftSide'
import ProductPageUiMisc from './ProductPageUiMisc/ProductPageUiMisc'
import useStore from '@/store'
import { useEffect, useRef, useState } from 'react'
import styles from './ProductPageUi.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import gsap from 'gsap'
import { PRODUCT_PAGE_BREAKPOINTS } from '@/sections/ProductPage/ProductPageDesktop/ProductPageDesktop'
import useBreakpoint from '@/hooks/use-breakpoint'

export const NEXT_POSITION_ID = 'nextPositionId-420'

const ProductPageUi = () => {
  const productPageData = useStore(state => state.productPageData)
  const setProductPageData = useStore(state => state.setProductPageData)
  const [productData, setProductData] = useState(null)
  const leftSideRef = useRef(null)
  const miscRef = useRef(null)
  const infoTableRef = useRef(null)
  const productDataSetDebounce = useRef()
  const enableDebounce = useRef()
  const productDataAnimateDebounce = useRef()
  const { isProductInnerPage, prevPathIsProductInnerPage } = useCurrentPage()
  const nextImagePositionRef = useRef(null)
  const [enable, setEnable] = useState(true)
  const { isMobile } = useBreakpoint()
  const productGalleryImagesOpen = useStore(state => state.productGalleryImagesOpen)
  const nextImageCoords = useStore(state => state.nextImageCoords)
  const setNextImageCoords = useStore(state => state.setNextImageCoords)
  const nextImage = useStore(state => state.nextImage)
  const setNextImage = useStore(state => state.setNextImage)

  useEffect(() => {
    if (!isProductInnerPage) {
      setNextImageCoords(null)
      setNextImage(null)
      return
    }

    if (nextImageCoords?.x !== 0 && nextImagePositionRef.current) {
      gsap.to(nextImagePositionRef.current, {
        opacity: 1,
        duration: 0.05,
        delay: 0.05,
      })
    }
  }, [nextImageCoords, isProductInnerPage, setNextImageCoords, setNextImage])

  useEffect(() => {
    if (!isProductInnerPage) {
      setProductPageData(null)
    }
  }, [isProductInnerPage, setProductPageData])

  const animateInItems = () => {
    if (leftSideRef.current) {
      leftSideRef.current.animateIn()
    }
    if (miscRef.current) {
      miscRef.current.animateIn()
    }
    if (infoTableRef.current) {
      infoTableRef.current.animateIn()
    }
  }

  const animateOutItems = () => {
    if (leftSideRef.current) {
      leftSideRef.current.animateOut()
    }
    if (miscRef.current) {
      miscRef.current.animateOut()
    }
    if (infoTableRef.current) {
      infoTableRef.current.animateOut()
    }
  }

  const resetItems = () => {
    if (leftSideRef.current) {
      leftSideRef.current.reset()
    }
    if (miscRef.current) {
      miscRef.current.reset()
    }
    if (infoTableRef.current) {
      infoTableRef.current.reset()
    }
  }

  useEffect(() => {
    if (productGalleryImagesOpen) {
      animateOutItems()
    } else {
      setTimeout(() => {
        animateInItems()
      }, 400)
    }
  }, [productGalleryImagesOpen])

  useEffect(() => {
    if (productDataSetDebounce.current) {
      clearTimeout(productDataSetDebounce.current)
    }

    if (productDataAnimateDebounce.current) {
      clearTimeout(productDataAnimateDebounce.current)
    }

    if (enableDebounce.current) {
      clearTimeout(enableDebounce.current)
    }

    const enableDebounceTimeout = 10
    const productDataSetDebounceTimeout = 600
    const productDataAnimateDebounceTimeout = 900

    let wasDisabled = false
    if (productPageData) {
      setEnable(prev => {
        wasDisabled = prev === false
        return true
      })
    }

    /*
    product-to-product transition
    */
    if (prevPathIsProductInnerPage && productPageData) {
      productDataSetDebounce.current = setTimeout(() => {
        resetItems()
        setProductData(productPageData)

        productDataAnimateDebounce.current = setTimeout(() => {
          animateInItems()
        }, 150)
      }, productDataSetDebounceTimeout * 0.7)
      return
    }

    /*
    regular transition
    */
    enableDebounce.current = setTimeout(() => {
      if (!wasDisabled) {
        animateOutItems()
      }

      productDataSetDebounce.current = setTimeout(() => {
        resetItems()

        if (!productPageData) {
          setTimeout(() => {
            setEnable(false)
            // wasDisabled.current = true
          }, 200)
          return
        }

        setProductData(productPageData)

        productDataAnimateDebounce.current = setTimeout(() => {
          animateInItems()
        }, productDataAnimateDebounceTimeout)
      }, productDataSetDebounceTimeout)
    }, enableDebounceTimeout)
  }, [productPageData, prevPathIsProductInnerPage])

  if (!productData || !enable || isMobile) return null

  return (
    <>
      <ProductPageUiLeftSide
        ref={leftSideRef}
        productData={productData}
        // className={classNames({ [styles.disable]: disable })}
      />
      <ProductPageUiMisc
        ref={miscRef}
        productData={productData}
        // className={classNames({ [styles.disable]: disable })}
      />
      <ProductPageUiInfoTable
        ref={infoTableRef}
        productData={productData}
        // materialsByCategory={materialsByCategory}
        // materialImagesByTitle={materialImagesByTitle}
        // className={classNames({ [styles.disable]: disable })}
      />
      <div className={styles.nextImagePositionContainer}>
        <div
          className={styles.nextImagePosition}
          ref={nextImagePositionRef}
          id={NEXT_POSITION_ID}
          style={{
            '--x': `${nextImageCoords?.x}px`,
            '--y': `${nextImageCoords?.y}px`,
            '--width': `${nextImageCoords?.width}px`,
            '--height': `${nextImageCoords?.height}px`,
          }}
        >
          {nextImage && (
            <SanityImage
              image={nextImage}
              breakpoints={PRODUCT_PAGE_BREAKPOINTS}
              className={styles.nextImagePosition__image}
              disableLoadedOpacity={true}
            />
          )}
        </div>
      </div>
    </>
  )
}

ProductPageUi.displayName = 'ProductPageUi'

export default ProductPageUi
